// App.tsx
import React, { useEffect } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './components/Login';
import OTPVerification from './components/OTPVerification';
import Dashboard from './components/Dashboard';
import Loading from './components/Loading';

const AppContent: React.FC = () => {
  const { isLoggedIn, isLoading, showOtpForm, userEmail, authState, checkAuthStatus } = useAuth();

  console.log('AppContent render:', { isLoggedIn, isLoading, showOtpForm, userEmail, authState });

  // Handle route-based authentication checks
  useEffect(() => {
    const handleRouteCheck = () => {
      const currentPath = window.location.pathname;
      console.log('Route change detected:', currentPath);
      
      // If trying to access dashboard or any protected route
      if (currentPath === '/dashboard' || currentPath === '/') {
        if (!isLoading && !checkAuthStatus()) {
          console.log('Unauthorized access attempt to protected route');
          window.history.pushState({}, '', '/login');
        }
      }
    };

    // Check on mount and when auth state changes
    if (!isLoading) {
      handleRouteCheck();
    }

    // Listen for browser navigation
    window.addEventListener('popstate', handleRouteCheck);
    
    return () => {
      window.removeEventListener('popstate', handleRouteCheck);
    };
  }, [isLoading, checkAuthStatus, authState]);

  // Handle URL changes based on auth state
  useEffect(() => {
    if (isLoading) return;

    const currentPath = window.location.pathname;
    
    if (authState === 'login' && currentPath !== '/login') {
      console.log('Navigating to login');
      window.history.pushState({}, '', '/login');
    } else if (authState === 'otp' && currentPath !== '/verify') {
      console.log('Navigating to OTP verification');
      window.history.pushState({}, '', '/verify');
    } else if (authState === 'authenticated' && (currentPath === '/login' || currentPath === '/verify' || currentPath === '/')) {
      console.log('Navigating to dashboard after authentication');
      window.history.pushState({}, '', '/dashboard');
      // Force a re-render to update the displayed component
      window.dispatchEvent(new PopStateEvent('popstate'));
    }
  }, [authState, isLoading]);

  if (isLoading) {
    return <Loading />;
  }

  // Handle routing based on current path and auth state
  const currentPath = window.location.pathname;
  
  console.log('Routing decision:', { currentPath, authState, isLoggedIn, showOtpForm });

  // Show OTP verification if in OTP state
  if (showOtpForm || authState === 'otp') {
    console.log('Showing OTP verification');
    return <OTPVerification email={userEmail} />;
  }

  // Show dashboard if authenticated
  if (isLoggedIn || authState === 'authenticated') {
    console.log('Showing dashboard');
    return (
      <ProtectedRoute>
        <Dashboard />
      </ProtectedRoute>
    );
  }

  // Default to login
  console.log('Showing login');
  return <Login />;
};

function App() {
  console.log('App component loaded - timestamp:', new Date().toISOString());
  
  // Handle initial route setup
  useEffect(() => {
    const currentPath = window.location.pathname;
    if (currentPath === '/') {
      window.history.replaceState({}, '', '/dashboard');
    }
  }, []);

  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
